import { type InputHTMLAttributes } from 'react'

// Chromium/Safari can mask a plain text field via CSS (-webkit-text-security),
// which keeps the browser password manager OUT entirely — it only offers to save
// (and autofills) real type="password" fields. Where that CSS is unsupported
// (Firefox) we fall back to a real password field so the secret is still masked,
// accepting the milder save prompt there.
const SUPPORTS_TEXT_SECURITY =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  (CSS.supports('-webkit-text-security', 'disc') || CSS.supports('text-security', 'disc'))

// `type` is owned by this component; everything else (value, onChange, onKeyDown,
// placeholder, autoFocus, className, disabled, …) passes straight through.
type SecretInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>

/**
 * Masked input for short-lived secrets — e-imza PIN, PFX şifresi — that must NEVER
 * be offered to or stored by the browser password manager. A masked text field is
 * invisible to credential autofill/save heuristics, unlike type="password".
 *
 * Do NOT use this for the portal account password (Login/Account) — there, saving
 * to the password manager is the desired behaviour.
 */
export default function SecretInput({ className, ...rest }: SecretInputProps) {
  // Attributes that further discourage third-party managers (1Password, LastPass,
  // Dashlane) regardless of the input type.
  const antiManager = {
    autoComplete: 'off',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: false,
    'data-lpignore': 'true',
    'data-1p-ignore': '',
    'data-form-type': 'other',
  } as const

  if (SUPPORTS_TEXT_SECURITY) {
    return (
      <input
        {...rest}
        {...antiManager}
        type="text"
        className={`secret-mask ${className ?? ''}`.trim()}
      />
    )
  }

  return <input {...rest} {...antiManager} type="password" className={className} />
}
